
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Course } from "@/types/course";

interface CourseDialogProps {
  course: Course;
  mentorName: string;
}

const CourseDialog = ({ course, mentorName }: CourseDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if the current user is the course owner (mentor)
  const isOwnProfile = user?.id === course.mentorId;

  const handleApply = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to apply for this course.",
        variant: "destructive",
      });
      return;
    }

    if (!message.trim()) {
      toast({
        title: "Message Required",
        description: "Please add a message explaining why you're interested in this course.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from("applications")
        .insert({
          user_id: user.id,
          course_id: course.id,
          mentor_id: course.mentorId,
          message: message.trim(),
          payment_amount: course.price,
          currency: 'aud' // Ensure AUD currency for applications
        });

      if (error) {
        console.error("Error submitting application:", error);
        toast({
          title: "Error",
          description: "Failed to submit your application. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Fetch user's profile to get their name for the notification
      const { data: userProfile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", user.id)
        .single();

      // Construct display name with fallback
      const displayName = userProfile?.first_name && userProfile?.last_name 
        ? `${userProfile.first_name} ${userProfile.last_name}`.trim()
        : "A user";

      // Create notification for the mentor
      await supabase
        .from("notifications")
        .insert({
          receiver_id: course.mentorId,
          type: "application",
          content: `New application for your course "${course.title}" from ${displayName}`,
          link: `/mentor-dashboard?tab=applications`,
        });

      toast({
        title: "Application Submitted!",
        description: `Your application for "${course.title}" has been sent to ${mentorName}.`,
      });

      setMessage("");
      setIsOpen(false);
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-lg">{course.title}</h3>
            <span className="text-xl font-bold text-emu-blue">A${course.price}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500 capitalize">
              {course.courseType === 'call' ? 'Voice/Video Call' : course.courseType}
            </span>
            {course.duration && (
              <span className="text-sm text-gray-500">
                {course.duration} minutes
              </span>
            )}
          </div>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{course.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Course Details</h4>
            <p className="text-sm text-gray-600 mb-2">
              {course.description || "No description available"}
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Type:</span>
                <span>{course.courseType === 'call' ? 'Voice/Video Call' : 'Subscription'}</span>
              </div>
              {course.courseType === 'subscription' && course.weeklyCallAllowance && (
                <div className="flex justify-between">
                  <span>{course.subscriptionType === 'monthly' ? 'Monthly' : 'Weekly'} Calls:</span>
                  <span>{course.weeklyCallAllowance}</span>
                </div>
              )}
              {course.duration && (
                <div className="flex justify-between">
                  <span>Duration:</span>
                  <span>{course.duration} minutes</span>
                </div>
              )}
            </div>
            <div className="mt-2">
              <span className="text-lg font-bold text-emu-blue">Price: A${course.price}</span>
              <p className="text-xs text-gray-500">All prices in Australian Dollars (AUD)</p>
            </div>
          </div>

          {/* Show different sections based on authentication and ownership */}
          {!isOwnProfile && user && (
            <>
              <div>
                <h4 className="font-medium mb-2">Apply for this course</h4>
                <Textarea
                  placeholder={`Tell ${mentorName} why you're interested in this course...`}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <Button
                onClick={handleApply}
                disabled={isSubmitting || !message.trim()}
                className="w-full bg-emu-blue hover:bg-emu-blue/90"
              >
                {isSubmitting ? "Submitting..." : "Apply Now"}
              </Button>
            </>
          )}

          {/* Show sign up prompt for public users */}
          {!isOwnProfile && !user && (
            <div className="text-center py-4">
              <h4 className="font-medium mb-2">Sign up now to apply</h4>
              <p className="text-sm text-gray-600 mb-4">
                Create an account to apply for this course with {mentorName}
              </p>
              <Button asChild className="w-full bg-emu-blue hover:bg-emu-blue/90">
                <Link to="/sign-up">Sign Up Now</Link>
              </Button>
            </div>
          )}

          {/* Show different message when viewing own profile */}
          {isOwnProfile && (
            <div className="text-center py-4">
              <p className="text-gray-600">This is your course</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CourseDialog;
